import { combineReducers } from 'redux';
import * as actions from '../actions/actionTypes';
import SrOddsConversion from '@bremen_webteam/srvg-oddsconversion'
import { IOddsState, IState, IMatchOddsState } from '../store/state';
import { IMatchOdds2Feed, IMatchOdds2Doc, IMatchOdds2Odds, IMatchOdds2Market } from '../types/IMatchOdds2Feed';
import { getActiveChannelId } from './gui';
import { getActiveMatchId, getPhaseIdByMatchId, getMatchIdByChannelId, getChannelIdByMatchId } from './timings';
import { getGamePointsByMatchId, getGameScoreByMatchId } from './matches';

const oddsConverter = SrOddsConversion.basicInit();

// a list of markets to filter the odds data to handle only the needed markets
const marketIdMapping = {
  // ['Winner'] : 186,
  ['{!setnr} set - winner'] : 202,
  // ['Correct score'] : 199,
  // ['{!setnr} set - game handicap'] : 203,
  // ['{!setnr} set - total games'] : 204,
  // ['{!setnr} set - correct score'] : 207,
  ['{!setnr} set game {gamenr} - winner'] : 210,
  // ['{!setnr} set game {gamenr} - to deuce'] : 212,
  // ['{!setnr} set game {gamenr} - correct score'] : 214,
  ['{!setnr} set game {gamenr} - {!pointnr} point'] : 218,
}

const initialOddsState : IOddsState = {};

const odds = (state:IOddsState = initialOddsState, action: any) : IOddsState => {
    switch (action.type) {
      case actions.FETCH_MATCH_ODDS_SUCCEEDED:
        return Object.assign({}, state, processOddsData(action.data, state ) )
      default:
        return state;
    }
};

// convert the response of the match_odds2 feed to an object containing the odds information
/*
  returns an object with the match id as property name and an array containing all available odds as content
 int (matchid): Array [
  0 : {id: int, name: string, status: int, favourite: int1, outcome: Array [...]},
  1 : {id: int, specifiers: string, name: string, status: int, favourite: int},
  n...
  ]
*/
export const processOddsData = ( oddsFeedData: IMatchOdds2Feed, state: IOddsState = initialOddsState ) => {
  // check if feed is available and if not return null
  if( !isFeadDataAvailable( oddsFeedData ) ) {
    return null
  }
  
  // process feed data to different variables
  let feedData: IMatchOdds2Odds = null
  let feedOdds: IMatchOdds2Market[] = []
  
  if (oddsFeedData.doc[0].event !== 'exception') {
    feedData = oddsFeedData.doc[0].data.odds
    feedOdds = feedData.market
  }

  const availableMarketIds = Object.values( marketIdMapping )
  const odds: IMatchOdds2Market[] = feedOdds.filter( ( odd: IMatchOdds2Market ) => availableMarketIds.includes( odd.id ) )
  
  // To get the match id we have to check the id
  // The id contains three information parts as one string with a ":" as seperator
  // currently the id is structured like this -> "vti:match:[matchId]"
  const feedIdParams: string[] = oddsFeedData.queryUrl.split('?')[0].split(':')
  
  // get the last parameter of the id split values.
  // The last parameter should be the match id
  const matchId = feedIdParams[ feedIdParams.length -1 ]
  const stateOdds: IMatchOdds2Market[] = Object.keys(state).includes(matchId) ? state[matchId].odds : [] // last odds
  const saveOdds: IMatchOdds2Market[] = Object.keys(state).includes(matchId) ? state[matchId].saved : [] // odds before last odds

  // map odds array to an object with an matchId index
  const data = { [ matchId ]: {
    odds: [...odds],
    timestamp: oddsFeedData.doc[0].timestamp || null,
    saved: [...stateOdds],
    expirationDate: feedData && feedData.odds_expiration_date ? 
			feedData.odds_expiration_date : 1
  } }
    
  return data
}

// fetch data of the match winner odds market of a certain game
/*
 returns an object containing data of the game winner odds market, structure see getSetWinnerOddsByMatchId
*/
export const getPointWinnerOddsByMatchId = (state: IState, matchId: number, oddType: string) =>  {
  try{
    const odds = getOddsByMatchId(state, matchId)
    const savedOdds = getOddsByMatchId(state, matchId, 'saved')
    const unifiedEventId: string = getUnifiedEventId(state, matchId)
    const channelId: number = getChannelIdByMatchId(state, matchId)
    const pointWinnerOdds = mapOddsObject(state, odds, savedOdds, 218, oddType, unifiedEventId, channelId )
    return pointWinnerOdds
  } catch (err) {
    // console.error('something went wrong', err)
    return []
  }
}

// fetch data of the match winner odds market of a certain game
/*
returns an object containing data of the game winner odds market, structure see getSetWinnerOddsByMatchId
*/
export const getMatchWinnerOddsByMatchId = ( state: IState, matchId: number, oddType: string ) =>  {
  const odds = getOddsByMatchId( state, matchId )
  const savedOdds = getOddsByMatchId(state, matchId, 'saved')
  const unifiedEventId: string = getUnifiedEventId(state, matchId)
  const channelId: number = getChannelIdByMatchId(state, matchId)
  const matchWinnerOdds = mapOddsObject(state, odds, savedOdds, 186, oddType, unifiedEventId, channelId )
  return matchWinnerOdds
}

// fetch data of the game winner odds market of a certain game
/*
  returns an object containing data of the game winner odds market, structure see getSetWinnerOddsByMatchId
*/
export const getGameWinnerOddsByMatchId = (state: IState, matchId: number, oddType: string ) =>  {
  const odds = getOddsByMatchId( state, matchId )
  const savedOdds = getOddsByMatchId(state, matchId, 'saved')
  const unifiedEventId: string = getUnifiedEventId(state, matchId)
  const channelId: number = getChannelIdByMatchId(state, matchId)
  const gameWinnerOdds = mapOddsObject(state, odds, savedOdds, 210, oddType, unifiedEventId, channelId )
  return gameWinnerOdds
}

// fetch data of the set winner odds market of a certain game
/*
  returns an object containing data of the set winner odds market
    id: int
    original:{ see getOdds returned data structure },
    specifiers: string,
    status: int,
    team: {
      away:{value: float, name: string, odds: string, active: int, id: string}
      home:{value: float, name: string, odds: string, active: int, id: string}
    }
*/
export const getSetWinnerOddsByMatchId = (state: IState, matchId: number, oddType: string ) =>  {
  const odds = getOddsByMatchId( state, matchId )
  const savedOdds = getOddsByMatchId(state, matchId, 'saved')
  const unifiedEventId: string = getUnifiedEventId(state, matchId)
  const channelId: number = getChannelIdByMatchId(state, matchId)
  const setWinnerOdds = mapOddsObject(state, odds, savedOdds, 202, oddType, unifiedEventId, channelId )
  return setWinnerOdds
}

const getCurrentOdd = (state: IState, odds: any, id: number, channel: number ) => {

  // we need to pick the right odd, depending on setnr/gamenr/pointnr as we have multible odds for this market now (next point etc.)
  let currentOdds = {}
  const matchId = getMatchIdByChannelId(state, channel)
  const gamescore = getGameScoreByMatchId(state, matchId)

  const current = {
    'set': gamescore.nthSetCounter,
    'game': gamescore.nthGameCounterOfSet,
    'point': getGamePointsByMatchId(state, matchId).nthPointCounter
  }

  const map = {
    202: 'setnr='+current['set'],
    210: 'setnr='+current['set']+'|gamenr='+current['game'],
    218: 'setnr='+current['set']+'|gamenr='+current['game']+'|pointnr='+current['point'],
  }

  if(map[id]) {
    for(let index in odds) {
      if(odds[index].specifiers == map[id]) {
        currentOdds = odds[index]
      }
    }
  }else {

    // TODO: fallback to old behaviour - do we need this ?
    currentOdds = getNewestOdd(odds)

  }

  return currentOdds
}

// takes an object containing odds, picks the one with the given id and reorders information into a usable format for us
/*
 returns an object containg one odd market and the according informations, structure see getSetWinnerOddsByMatchId
*/
const mapOddsObject = (state: IState, odds: any, savedOdds:any, id: number, oddType: string, unifiedEventId: string, channelId: number) => {

  const currentOdd: any = getCurrentOdd( state, filterOddsById(odds, id), id, channelId )

  const outcome = !isObjectEmpty(currentOdd) ? currentOdd.outcome : {}
  const home = !isObjectEmpty( outcome ) ? outcome[0] : {}
  const away = !isObjectEmpty( outcome ) ? outcome[1] : {}

  const homeOddsValue: number = Number(checkOddsValue({ home, away }, 'home'))
  const awayOddsValue: number = Number(checkOddsValue({ home, away }, 'away'))

  const homeName: string = !isObjectEmpty( home ) ? home.name : ''
  const awayName: string = !isObjectEmpty( away ) ? away.name : ''

  const homeOdds: string = checkOddsValue({ home, away }, 'home')
  const awayOdds: string = checkOddsValue({ home, away }, 'away')

  const homeActive: string = !isObjectEmpty( home ) ? home.active : -1
  const awayActive: string = !isObjectEmpty( away ) ? away.active : -1

  const homeId: string = !isObjectEmpty( home ) ? home.id : ''
  const awayId: string = !isObjectEmpty( away ) ? away.id : ''

  const status: number = currentOdd ? currentOdd.status : 0

  const specifiers: string = currentOdd ? currentOdd.specifiers : ''

  const oddId: number = currentOdd ? currentOdd.id : null

  const currentSavedOdds: any = getCurrentOdd( state, filterOddsById(savedOdds, id), id, channelId)

  const savedOutcome = !isObjectEmpty(currentSavedOdds) ? currentSavedOdds.outcome : {}
  const savedHome = !isObjectEmpty(savedOutcome) ? savedOutcome[0] : {}
  const savedAway = !isObjectEmpty(savedOutcome) ? savedOutcome[1] : {}

  const savedHomeOddsValue: number = Number(checkOddsValue({ home: savedHome, away: savedAway }, 'home'))
  const savedAwayOddsValue: number = Number(checkOddsValue({ home: savedHome, away: savedAway }, 'away'))

  const trendHome: number = calculateTrend({ home: homeOddsValue, away: awayOddsValue }, { home: savedHomeOddsValue, away: savedAwayOddsValue }, 'home')
  const trendAway: number = calculateTrend({ home: homeOddsValue, away: awayOddsValue }, { home: savedHomeOddsValue, away: savedAwayOddsValue }, 'away')
  

  const mappedOdds = {
    team: {
      home: {
        value: homeOddsValue,
        name: homeName,
        odds: homeOdds ? parseOddTooddType(homeOdds, oddType): '',
        active: homeActive,
        id: homeId,
        trend: trendHome
      },
      away: {
        value: awayOddsValue,
        name: awayName,
        odds: awayOdds ? parseOddTooddType(awayOdds, oddType) : '',
        active: awayActive,
        id: awayId,
        trend: trendAway
      }
    },
    status: status,
    specifiers: specifiers,
    id: oddId,
    original: { ...currentOdd },
    unifiedEventId
  }

  return mappedOdds
}

const parseOddTooddType = (odd: string, type: string ) => oddsConverter.convertOdds(odd, type)

const calculateTrend = ( oddsValues: any, savedOddsValues: any, homeAway: string ) => {
  // if there was no data saved a trend makes no sense
  if (!savedOddsValues['home'] || !savedOddsValues['away'] ) {
    return 0
  }
 
  const trendValues = { 
    home: savedOddsValues['home'] - oddsValues['home'], 
    away: savedOddsValues['away'] - oddsValues['away']
  }

  return trendValues[homeAway]
}

const checkOddsValue = ( odds: any, homeAway: string) => {
  const unvalidOdd = Object.values(odds).find((odd:any) => parseFloat(odd.odds) <= 1)

  if (unvalidOdd || !Object.values(odds).length ) {
    return ''
  }

  return !isObjectEmpty(odds[homeAway]) ? odds[homeAway].odds : ''
}

const checkOddsActiveState = ( home: any, away: any ) => {
  if( isObjectEmpty( home ) || isObjectEmpty( away ) ) {
    return 0
  }

  if( home.odds === '' || away.odds === '' ) {
    return 0
  }

  if( !home.active || !away.active ) {
    return 0
  }

  return 1
}


// state id object is empty or not
/*
  returns a boolean
*/
const isObjectEmpty = ( obj: any ) => {
  return (
    Object.keys( obj ).length
      ? false
      : true
  )
}

// This function sort the outcome objects by the specifyer. The order is high to low
export const sortOdds = (pointOdds: any) => {
  const tmpOdds = [...pointOdds]
  return tmpOdds.sort((a: any, b: any) => generateSortableSpecifier(b.specifiers) - generateSortableSpecifier(a.specifiers))
}

// this function generates a sortable number out of the specifier string
// to do that it splits the string to the final numbers and shift each number by 1000 to have a sortable number
const generateSortableSpecifier = (specifiers: string) => {
  return specifiers.split('|')
    .map((specifier: string) => Number(specifier.split('=')[1]))
    .reduce((accumulator: number, value: number, index: number) => accumulator * 1000 + value, 0)
}

// fetch all odds for a specific match id
/*
  returns an objecet containing all available odds of a certain match
  each odd is reflected by an object property, structure listed in getOdds
*/
const getOddsByMatchId = ( state: IState, matchId: number, property: string = 'odds' ) => {
  try {
    return getOdds( state )[ matchId ][property as keyof IMatchOddsState]
  } catch ( err ) {
    // console.error( 'something went wrong!', err )
    return []
  }
}

export const getUnifiedEventId = ( state: IState, matchId: number ) => {
  try {
    return (state.odds[matchId] as any).unifiedEventId // FIXME this is wrong, will always return undefined
  } catch ( err ) {
     // console.error( 'something went wrong!', err )
    return []
  }
}

// fetch all odds for all matches
/*
  returns an object containing all matches and the according, available odds
  each odd is reflected by an object property looking listed from 0 - n
    favourite (OPTIONAL): int,
    id:      int,
    name:    string,
    outcome: Array [
      0:{id: string, name: string, odds: string, result: int, active: int},
      n: ...
    ],
    specifiers (OPTIONAL): string,
    status: int
*/
const getOdds = ( state: IState ) => {
  return state.odds
}

// fetches the last odd
/*
 returns an objecet containing data of the last odd of a list off odds, structure listed in getOdds
*/
const getNewestOdd = ( odds: any ) => {
  return sortOdds(odds).length ? sortOdds(odds)[ 0 ] : {}
}

// picks a certain odd out of all available odds depending on the id
/*
  returns an object containing data of a single odd, structure listed in getOdds
*/
const filterOddsById = ( odds: any, id: number ) => {
  try{
    return odds.filter( ( odd: any ) => odd.id === id )
  } catch ( err ) {
    // console.error( 'something went wrong ', err )
    return []
  }
}

// fetch feed state
/*
  returns a boolean
*/
const isFeadDataAvailable = ( data: any ) => {
  return data.doc[0].data ? true : false
}

export const getExpirationDateByMatchId = (state: IState, matchId: number) => {
  try{
    // debugger
    return state.odds[matchId].expirationDate
  } catch (err) {
    return null
  }
}

export default odds;
